import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Button, TextField, Paper, Snackbar, Grid, Typography } from '@mui/material';
import { styled } from '@mui/system';
import API_URL from '../apiConfig';

const BakeryButton = styled(Button)({
    background: 'linear-gradient(45deg, #e88ba9 0%, #f4a2bc 100%)',
    borderRadius: '20px',
    border: 0,
    color: 'white',
    padding: '8px 24px',
    boxShadow: '0 4px 10px rgba(232, 139, 169, 0.3)',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
        background: 'linear-gradient(45deg, #f4a2bc 0%, #e88ba9 100%)',
        boxShadow: '0 6px 15px rgba(232, 139, 169, 0.4)',
    },
});

const OutlineButton = styled(Button)({
    borderRadius: '20px',
    color: '#d46a89',
    borderColor: '#e88ba9',
    padding: '7px 19px',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
        borderColor: '#d46a89',
        backgroundColor: '#fcf4f4',
    },
});

const SoftTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '&.Mui-focused fieldset': {
            borderColor: '#e88ba9',
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#e88ba9',
    }
});

const AdminCakes = () => {
    const [cakes, setCakes] = useState([]);
    const [cake_id, setCakeId] = useState("");
    const [name, setName] = useState("");
    const [price, setPrice] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("");
    const [image, setImage] = useState(""); // Missing image field in DB
    const [open, setOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    const handleClick = async (e) => {
        e.preventDefault();

        if (name === '' || price === '' || description === '' || category === '') {
            setAlertMessage('Please fill out all fields except Cake ID!');
            setOpen(true);
            return;
        }

        const cakeData = {
            id: cake_id,
            name,
            price,
            description,
            category,
            imageUrl: image || "https://images.unsplash.com/photo-1578985545062-69928b1d9587?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" // Default image if none provided
        };

        try {
            await axios.post(`${API_URL}/api/cakes`, cakeData, { headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') } });
            fetchCakes();
            setAlertMessage(cake_id ? 'Cake updated successfully!' : 'Cake added successfully!');
            setOpen(true);
            handleClear();
        } catch (error) {
            console.error('Error adding/updating cake:', error);
            setAlertMessage('Error connecting to database');
            setOpen(true);
        }
    };

    const handleEdit = (cake) => {
        setCakeId(cake._id);
        setName(cake.name);
        setPrice(cake.price);
        setDescription(cake.description);
        setCategory(cake.category);
        setImage(cake.imageUrl || "");
    };

    const handleClear = () => {
        setCakeId("");
        setName("");
        setPrice("");
        setDescription("");
        setCategory("");
        setImage("");
    };

    const handleClose = (e, reason) => {
        if (reason === 'clickaway') return;
        setOpen(false);
    };

    useEffect(() => {
        fetchCakes();
    }, []);

    const fetchCakes = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/cakes`);
            setCakes(response.data);
        } catch (error) {
            console.error('Error fetching cakes:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this cake?")) return;
        try {
            await axios.delete(`${API_URL}/api/cakes/${id}`,
                { headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') } });
            fetchCakes();
        } catch (error) {
            console.error('Error deleting cake:', error);
        }
    };

    const columns = [
        {
            field: 'imageUrl',
            headerName: 'Pic',
            width: 80,
            renderCell: (params) => (
                <Box
                    component="img"
                    sx={{ height: 40, width: 40, borderRadius: '8px', objectFit: 'cover', mt: 1 }}
                    alt={params.row.name}
                    src={params.row.imageUrl || 'https://via.placeholder.com/40x40/fcf4f4/d46a89?text=Cake'}
                />
            )
        },
        { field: 'name', headerName: 'Name', width: 200 },
        { field: 'price', headerName: 'Price (LKR)', width: 120 },
        { field: 'category', headerName: 'Category', width: 150 },
        { field: 'description', headerName: 'Description', width: 350 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            renderCell: (params) => (
                <Box display="flex" gap="8px" alignItems="center" height="100%">
                    <OutlineButton variant="outlined" size="small" onClick={() => handleEdit(params.row)}>
                        Edit
                    </OutlineButton>
                    <Button sx={{ minWidth: 0, p: '4px 10px', color: '#e53935' }} size="small" onClick={() => handleDelete(params.row._id)}>
                        ✕
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <React.Fragment>
            <Typography variant="h4" sx={{ fontFamily: '"Playfair Display", serif', color: '#d46a89', mb: 3 }}>
                Menu Management
            </Typography>

            <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: '24px', border: '1px solid #fcf4f4', background: '#fff' }}>
                <Typography variant='h6' sx={{ color: '#5c434a', mb: 3, fontWeight: 700 }}>
                    {cake_id ? 'Update Existing Cake' : 'Add New Bakery Item'}
                </Typography>

                <Box component='form' noValidate>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <SoftTextField
                                fullWidth
                                disabled
                                size="small"
                                name='cake_id'
                                label="Cake ID (Auto-generated)"
                                value={cake_id || "New Item"}
                                sx={{ background: '#fcf4f4' }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SoftTextField
                                fullWidth
                                size="small"
                                name='category'
                                label="Category (e.g., Bento Cake, Cake, Build Your Own Cake)"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <SoftTextField
                                fullWidth
                                size="small"
                                name='name'
                                label="Item Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SoftTextField
                                fullWidth
                                size="small"
                                name='price'
                                label="Price (LKR)"
                                type="number"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <SoftTextField
                                fullWidth
                                size="medium"
                                multiline
                                rows={2}
                                name='description'
                                label="Delicious Description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <SoftTextField
                                fullWidth
                                size="small"
                                name='image'
                                label="Image URL (Optional)"
                                value={image}
                                onChange={(e) => setImage(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} display="flex" gap={2} mt={1}>
                            <BakeryButton onClick={handleClick}>
                                {cake_id ? 'Update Item' : 'Add to Menu'}
                            </BakeryButton>
                            <OutlineButton variant="outlined" onClick={handleClear}>
                                Clear Form
                            </OutlineButton>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, borderRadius: '24px', border: '1px solid rgba(232, 139, 169, 0.2)', boxShadow: '0 10px 30px rgba(0,0,0,0.02)' }}>
                <DataGrid
                    rows={cakes}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 20]}
                    disableSelectionOnClick
                    getRowId={(row) => row._id}
                    autoHeight
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#fffafb',
                            color: '#8c7077',
                            fontSize: '14px',
                            fontWeight: 700,
                            borderBottom: '2px solid #fcf4f4'
                        },
                        '& .MuiDataGrid-row': {
                            '&:hover': {
                                backgroundColor: '#fffafb',
                            },
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #fcf4f4',
                            color: '#5c434a'
                        }
                    }}
                />
            </Paper>

            <Snackbar
                message={alertMessage}
                autoHideDuration={4000}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                ContentProps={{ sx: { background: '#fff', color: '#d46a89', fontWeight: 'bold', border: '1px solid #f4a2bc' } }}
            />
        </React.Fragment>
    );
};

export default AdminCakes;
